/**
 * Base array of all string-valued user model property keys.
 * Single source of truth for user field names across the codebase.
 */
const userModelFieldsArray = [
    'id',
    'email',
    'identifierEmail',
    'firstName',
    'lastName',
    'externalId',
    'instagramAccountId',
    'googleAccountName',
];
/** Iterable array of all user model fields */
export const userModelFields = [...userModelFieldsArray];
/**
 * Fields that can be used to uniquely identify/lookup a user.
 * Used for wildcard search, user lookup queries.
 */
const uniqueUserIdentifierKeysArray = [
    'id',
    'email',
    'identifierEmail',
    'instagramAccountId',
    'externalId',
];
// Export the array
export const uniqueUserIdentifierKeys = [...uniqueUserIdentifierKeysArray];
// Define identifier type configurations
const identifierTypes = {
    id: {
        key: 'id',
        detect: (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value), // UUID pattern
        process: (value) => value
    },
    email: {
        key: 'email',
        detect: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
        process: (value) => value
    },
    instagramAccountId: {
        key: 'instagramAccountId',
        detect: (value) => /^@/.test(value) || value.toLowerCase().includes('instagram'),
        process: (value) => value.replace(/^@/, '') // Remove @ if present for consistency
    },
    externalId: {
        key: 'externalId',
        detect: () => true, // Default fallback
        process: (value) => value
    }
};
/**
 * Register a new identifier type configuration
 * @param key The identifier key
 * @param config The identifier configuration (without the key)
 */
export const registerIdentifierType = (key, config) => {
    if (!uniqueUserIdentifierKeysArray.includes(key)) {
        console.warn(`Warning: Registering identifier type '${key}' that is not in uniqueUserIdentifierKeys`);
    }
    identifierTypes[key] = {
        key,
        ...config
    };
};
/**
 * Detects the type of identifier from a wildcard input
 * @param wildcardValue The value to analyze
 * @returns Object containing the type and processed value
 */
export const detectIdentifierType = (wildcardValue) => {
    if (wildcardValue === null || wildcardValue === undefined) {
        return { type: 'externalId', value: '' };
    }
    const value = String(wildcardValue);
    // Try each identifier type in priority order (id first to catch UUIDs)
    for (const type of ['id', 'email', 'instagramAccountId', 'externalId']) {
        const config = identifierTypes[type];
        if (config.detect(value)) {
            return { type, value: config.process(value) };
        }
    }
    // Default fallback should never be reached due to externalId's detect always returning true
    return { type: 'externalId', value };
};
/**
 * Checks if an object is a properly formed UserIdentifierObject
 * @param obj Object to check
 * @returns Boolean indicating if the object is a UserIdentifierObject
 */
export const isUserIdentifierObject = (obj) => {
    return obj &&
        obj.type === 'identifierObject' &&
        uniqueUserIdentifierKeys.every(key => key in obj);
};
/**
 * Creates a UserIdentifierObject with all required fields, setting missing values to null
 * @param params Partial parameters for the identifier object
 * @returns A properly formed UserIdentifierObject
 */
export const createUserIdentifierObject = (params) => {
    // Extract the wildcard or use empty string
    const wildcard = params.wildcard || '';
    // Create base object with all identifier fields set to null
    const identifierObject = {
        type: 'identifierObject',
        id: null,
        email: null,
        instagramAccountId: null,
        externalId: null,
        identifierEmail: null,
        wildcard
    };
    // Override nulls with any provided values
    uniqueUserIdentifierKeys.forEach(key => {
        if (params[key]) {
            identifierObject[key] = params[key];
        }
    });
    // If wildcard is provided but no specific fields, auto-detect the field type
    if (wildcard && !params.id && !params.email && !params.instagramAccountId && !params.externalId) {
        const detected = detectIdentifierType(wildcard);
        identifierObject[detected.type] = detected.value;
    }
    return identifierObject;
};
//# sourceMappingURL=unique-user-identifier-keys.js.map